<script setup>
import { computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import {
  categoryFor,
  nameWithoutCategory,
  UNCATEGORIZED_KEY,
  useCannedResponseUsage,
} from 'dashboard/composables/useCannedResponseGroups';
import MentionBox from '../mentions/MentionBox.vue';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  searchKey: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['replace']);

const store = useStore();
const { t } = useI18n();
const { getPlainText } = useMessageFormatter();
const cannedMessages = useMapGetter('getCannedResponses');
const { recentlyUsedCodes, isFavorite, toggleFavorite, recordUsage } =
  useCannedResponseUsage();

const toItem = cannedMessage => ({
  label: nameWithoutCategory(cannedMessage.short_code),
  key: cannedMessage.short_code,
  description: cannedMessage.content,
  category: categoryFor(cannedMessage.short_code),
});

// While the agent hasn't typed a search term yet, surface their recently
// used responses first (most useful, zero-typing shortcut for repeat
// sends) - once they start searching, relevance matters more than recency
// so the plain alphabetical-by-category order takes over.
const items = computed(() => {
  const allItems = cannedMessages.value.map(toItem);
  if (props.searchKey) return allItems;

  const recentItems = recentlyUsedCodes.value
    .map(code => allItems.find(item => item.key === code))
    .filter(Boolean);
  if (!recentItems.length) return allItems;

  const recentKeys = new Set(recentItems.map(item => item.key));
  return [
    ...recentItems,
    ...allItems.filter(item => !recentKeys.has(item.key)),
  ];
});

const categoryLabel = category =>
  category === UNCATEGORIZED_KEY
    ? t('CANNED_RESPONSE.UNCATEGORIZED')
    : category;

const fetchCannedResponses = () => {
  store.dispatch('getCannedResponse', { searchKey: props.searchKey });
};

watch(() => props.searchKey, fetchCannedResponses);
onMounted(fetchCannedResponses);

const handleMentionClick = (item = {}) => {
  recordUsage(item.key);
  emit('replace', item.description);
};
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <MentionBox
    v-if="items.length"
    :items="items"
    @mention-select="handleMentionClick"
  >
    <template #default="{ item, selected }">
      <div class="flex items-center justify-between gap-2 w-full">
        <div class="min-w-0 flex-1">
          <p
            class="max-w-full min-w-0 mb-0 overflow-hidden text-sm font-medium text-n-slate-11 group-hover:text-n-slate-12 text-ellipsis whitespace-nowrap"
            :class="{ 'text-n-slate-12': selected }"
          >
            {{ getPlainText(item.description) }}
          </p>
          <p
            class="max-w-full min-w-0 mb-0 overflow-hidden text-xs text-n-slate-11 group-hover:text-n-slate-12 text-ellipsis whitespace-nowrap"
            :class="{ 'text-n-slate-12': selected }"
          >
            <span
              class="inline-block px-1 py-0.5 mr-1 rounded bg-n-alpha-2 text-[10px] uppercase tracking-wide"
              >{{ categoryLabel(item.category) }}</span
            >/{{ item.label }}
          </p>
        </div>
        <!--
          Not a <button>: MentionBox's own item is already a <button> (see
          MentionBox.vue) and nested buttons are invalid HTML - browsers
          silently restructure the DOM to close the outer button early,
          which breaks click targeting for both elements. role="button" +
          keydown gives the same interactivity/accessibility without the
          nesting problem.
        -->
        <span
          class="shrink-0 p-1 text-n-slate-10 hover:text-n-amber-9 cursor-pointer"
          :class="{ 'text-n-amber-9': isFavorite(item.key) }"
          role="button"
          tabindex="0"
          :title="t('CANNED_RESPONSE.TOGGLE_FAVORITE')"
          @click.stop="toggleFavorite(item.key)"
          @keydown.enter.stop="toggleFavorite(item.key)"
          @keydown.space.stop.prevent="toggleFavorite(item.key)"
        >
          <Icon
            :icon="isFavorite(item.key) ? 'i-lucide-star' : 'i-lucide-star-off'"
            class="size-3.5"
          />
        </span>
      </div>
    </template>
  </MentionBox>
</template>
